import React, { useState, useContext } from 'react';
import axios from 'axios';
import { AuthContext } from '@/App';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { User, Key, Mail, Shield } from 'lucide-react';

const MyAccountPage = () => {
  const { API, token, user } = useContext(AuthContext);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [saving, setSaving] = useState(false);

  const handleChangePassword = async (e) => {
    e.preventDefault();

    // Validierung
    if (newPassword.length < 6) {
      toast.error('Das neue Passwort muss mindestens 6 Zeichen lang sein');
      return;
    }

    if (newPassword !== confirmPassword) {
      toast.error('Die Passwörter stimmen nicht überein');
      return;
    }

    setSaving(true);
    try {
      await axios.put(
        `${API}/users/change-password`,
        {
          current_password: currentPassword,
          new_password: newPassword,
        },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      
      toast.success('Passwort erfolgreich geändert!');
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Fehler beim Ändern des Passworts';
      toast.error(errorMsg);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6 animate-slide-in max-w-2xl mx-auto">
      <div>
        <h1 className="text-4xl font-bold mb-2" style={{ fontFamily: 'Space Grotesk' }}>
          Mein Konto
        </h1>
        <p className="text-slate-600">Verwalten Sie Ihre Kontodaten</p>
      </div>

      {/* Benutzerinformationen */}
      <Card className="p-6">
        <div className="flex items-center space-x-2 mb-4">
          <User className="text-blue-600" size={24} />
          <h2 className="text-xl font-semibold">Persönliche Informationen</h2>
        </div>
        
        <div className="space-y-4">
          <div>
            <Label className="flex items-center space-x-2 mb-1">
              <Mail size={16} />
              <span>E-Mail</span>
            </Label>
            <Input value={user?.email || ''} disabled className="bg-slate-50" />
          </div>
          
          <div>
            <Label className="flex items-center space-x-2 mb-1">
              <User size={16} />
              <span>Name</span>
            </Label>
            <Input value={user?.name || ''} disabled className="bg-slate-50" />
          </div>
          
          <div>
            <Label className="flex items-center space-x-2 mb-1">
              <Shield size={16} />
              <span>Rolle</span>
            </Label>
            <Input 
              value={user?.role === 'admin' ? 'Administrator' : 'Helfer'} 
              disabled 
              className="bg-slate-50" 
            />
          </div>
        </div>
      </Card>

      {/* Passwort ändern */}
      <Card className="p-6">
        <div className="flex items-center space-x-2 mb-4">
          <Key className="text-blue-600" size={24} />
          <h2 className="text-xl font-semibold">Passwort ändern</h2>
        </div>
        
        <form onSubmit={handleChangePassword} className="space-y-4">
          <div>
            <Label>Aktuelles Passwort</Label>
            <Input
              type="password"
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
              required
              placeholder="Aktuelles Passwort eingeben"
            />
          </div>
          
          <div>
            <Label>Neues Passwort</Label>
            <Input
              type="password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              required
              placeholder="Neues Passwort eingeben"
              minLength={6}
            />
            <p className="text-xs text-slate-500 mt-1">Mindestens 6 Zeichen</p>
          </div>
          
          <div>
            <Label>Passwort bestätigen</Label>
            <Input
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              required
              placeholder="Neues Passwort bestätigen"
              minLength={6}
            />
          </div>
          
          <Button
            type="submit"
            disabled={saving}
            className="w-full bg-gradient-to-r from-blue-500 to-indigo-600"
          >
            {saving ? 'Wird gespeichert...' : 'Passwort ändern'}
          </Button>
        </form>
      </Card>
    </div>
  );
};

export default MyAccountPage;
